import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Scale,
  UserRound,
  Banknote,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { PageSearchBar } from '../components/common/PageSearchBar';
import { StatsCard } from '../components/common/StatsCard';
import { MobileModalPortal } from '../components/common/MobileModalPortal';
import { AmountInput } from '../components/common/AmountInput';
import { parseAmountInput } from '../lib/formatInr';
import { useCanApproveWaivers, useCanWrite } from '../lib/permissions';
import { useTranslation } from '../lib/i18n';
import {
  useUnifiedCustomers,
  useAdminEmiPayments,
  recordOverdueWaiver,
  type UnifiedCustomer,
  type UnifiedLoanFacility,
} from '../lib/workspaceStore';
import {
  computeCustomerOverdueAnalysis,
  computeWorkspaceOverdueSummary,
  formatDisplayDate,
  type OverdueInstallmentRow,
} from '../lib/overdue';
import { ExportMenu, type ExportRow } from '../lib/exportReports';
import { adminEntryIso, defaultAdminEntryDate } from '../lib/workspaceAsOf';

interface OverdueManagementProps {
  setActiveItem?: (id: string) => void;
  onOpenCustomer?: (id: string) => void;
}

type CustomerOverdueRow = {
  customer: UnifiedCustomer;
  analysis: ReturnType<typeof computeCustomerOverdueAnalysis>;
};

function formatInr(n: number) {
  return `₹${Math.round(Math.max(0, n)).toLocaleString('en-IN')}`;
}

export function OverdueManagement({ setActiveItem, onOpenCustomer }: OverdueManagementProps) {
  const { t } = useTranslation();
  const canWriteModule = useCanWrite('overdue');
  const canApprove = useCanApproveWaivers();
  const canWrite = canWriteModule && canApprove;
  const customers = useUnifiedCustomers();
  const payments = useAdminEmiPayments();
  const [query, setQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [waiveTarget, setWaiveTarget] = useState<{
    customer: UnifiedCustomer;
    facility: UnifiedLoanFacility;
    maxAmount: number;
  } | null>(null);

  const summary = useMemo(() => computeWorkspaceOverdueSummary(customers, payments), [customers, payments]);

  const rows = useMemo(() => {
    const out: CustomerOverdueRow[] = [];
    for (const c of customers) {
      const analysis = computeCustomerOverdueAnalysis(c, payments);
      if (analysis.ledgerRows.length === 0 && analysis.totalRemaining <= 0) continue;
      if (analysis.totalRemaining <= 0 && analysis.totalWaived <= 0 && analysis.totalCollected <= 0) {
        if (analysis.rows.length === 0) continue;
      }
      out.push({ customer: c, analysis });
    }
    out.sort((a, b) => b.analysis.totalRemaining - a.analysis.totalRemaining);
    return out;
  }, [customers, payments]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.customer.name.toLowerCase().includes(q) ||
        r.customer.phone?.includes(q) ||
        r.analysis.ledgerRows.some((l) => l.facility.toLowerCase().includes(q))
    );
  }, [rows, query]);

  const exportRows: ExportRow[] = useMemo(() => {
    const out: ExportRow[] = [];
    for (const { customer, analysis } of filtered) {
      for (const l of analysis.ledgerRows) {
        if (l.originalAmount <= 0 && l.remainingBalance <= 0) continue;
        out.push({
          Customer: customer.name,
          Phone: customer.phone || '',
          Facility: l.facility,
          Installment: l.installmentNo,
          DueDate: l.dueDate,
          PaidDate: l.paidDate || '',
          OverdueDays: l.overdueDays,
          EmiAmount: l.emiAmount,
          OriginalOverdue: l.originalAmount,
          Waived: l.waivedAmount,
          Collected: l.collectedAmount,
          Remaining: l.remainingBalance,
          Status: l.status === 'unpaid_overdue' ? 'Unpaid overdue' : 'Paid late',
        });
      }
    }
    return out;
  }, [filtered]);

  const openCustomer = (id: string) => {
    if (onOpenCustomer) onOpenCustomer(id);
    else setActiveItem?.('customers');
  };

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-8 sm:space-y-5">
      <header className="app-page-header">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-rose-700 dark:text-rose-400">
            {t('hub.overdueManagement')}
          </p>
          <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 sm:text-2xl">
            Overdue management desk
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-zinc-600 dark:text-zinc-400">
            Formula: (Overdue days × EMI amount) ÷ 365. Days accrue from due date until payment or books-as-of for
            unpaid installments.
          </p>
        </div>
        <ExportMenu
          title="Overdue ledger export"
          excelFilename="overdue-management.xls"
          rows={exportRows}
          disabled={exportRows.length === 0}
        />
      </header>

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4 lg:gap-3">
        <StatsCard
          title="Customers"
          titleLong="With overdue balance"
          value={String(summary.customersWithOverdue)}
          icon={UserRound}
          color="text-rose-700"
          accent="border-l-rose-600"
        />
        <StatsCard
          title="Remaining"
          titleLong="Outstanding overdue"
          value={formatInr(summary.totalRemaining)}
          icon={AlertTriangle}
          color="text-rose-700"
          accent="border-l-rose-500"
        />
        <StatsCard
          title="Collected"
          titleLong="Overdue collected"
          value={formatInr(summary.totalCollected)}
          icon={Banknote}
          color="text-emerald-700"
          accent="border-l-emerald-600"
        />
        <StatsCard
          title="Waived"
          titleLong="Overdue waived"
          value={formatInr(summary.totalWaived)}
          icon={Scale}
          color="text-violet-700"
          accent="border-l-violet-600"
        />
      </div>

      <PageSearchBar value={query} onChange={setQuery} placeholder="Search customer, phone, or facility…" />

      <div className="app-table-wrap">
        <table className="app-table min-w-full">
          <thead>
            <tr>
              <th className="w-8" />
              <th>Customer</th>
              <th className="text-right">Unpaid days</th>
              <th className="text-right">Original</th>
              <th className="text-right">Waived</th>
              <th className="text-right">Collected</th>
              <th className="text-right">Remaining</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-12 text-center text-sm text-zinc-500">
                  No overdue installments in the current books view.
                </td>
              </tr>
            ) : (
              filtered.map(({ customer, analysis }) => {
                const open = expandedId === customer.id;
                return (
                  <React.Fragment key={customer.id}>
                    <tr className={analysis.totalRemaining > 0 ? 'bg-rose-50/40 dark:bg-rose-950/20' : undefined}>
                      <td>
                        <button
                          type="button"
                          onClick={() => setExpandedId(open ? null : customer.id)}
                          className="rounded p-1 text-zinc-500 hover:bg-zinc-100"
                          aria-label={open ? 'Collapse' : 'Expand'}
                        >
                          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => openCustomer(customer.id)}
                          className="text-left font-medium text-zinc-900 hover:text-emerald-700 dark:text-zinc-100"
                        >
                          {customer.name}
                        </button>
                        {customer.phone ? (
                          <p className="text-xs text-zinc-500">{customer.phone}</p>
                        ) : null}
                      </td>
                      <td className="text-right tabular-nums font-semibold text-rose-900">{analysis.unpaidOverdueDays}</td>
                      <td className="text-right tabular-nums">{formatInr(analysis.totalPenaltyAmount)}</td>
                      <td className="text-right tabular-nums text-violet-800">{formatInr(analysis.totalWaived)}</td>
                      <td className="text-right tabular-nums text-emerald-800">{formatInr(analysis.totalCollected)}</td>
                      <td className="text-right tabular-nums font-semibold text-rose-900">
                        {formatInr(analysis.totalRemaining)}
                      </td>
                      <td className="text-right">
                        <button
                          type="button"
                          onClick={() => openCustomer(customer.id)}
                          className="text-xs font-semibold text-emerald-700 hover:underline"
                        >
                          Open profile
                        </button>
                      </td>
                    </tr>
                    {open ? (
                      <tr>
                        <td colSpan={8} className="bg-zinc-50/80 px-3 py-3 dark:bg-zinc-900/80">
                          <InstallmentLedgerTable
                            rows={analysis.ledgerRows.filter((r) => r.originalAmount > 0 || r.remainingBalance > 0)}
                            customer={customer}
                            canWrite={canWrite}
                            onWaive={(facility, maxAmount) =>
                              setWaiveTarget({ customer, facility, maxAmount })
                            }
                          />
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {waiveTarget && canWrite ? (
        <OverdueWaiveModal
          customer={waiveTarget.customer}
          facility={waiveTarget.facility}
          maxAmount={waiveTarget.maxAmount}
          onClose={() => setWaiveTarget(null)}
        />
      ) : null}
    </div>
  );
}

function InstallmentLedgerTable({
  rows,
  customer,
  canWrite,
  onWaive,
}: {
  rows: OverdueInstallmentRow[];
  customer: UnifiedCustomer;
  canWrite: boolean;
  onWaive: (facility: UnifiedLoanFacility, maxAmount: number) => void;
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-zinc-500">No overdue installment rows for this customer.</p>;
  }

  const facilityRemaining = new Map<string, number>();
  for (const r of rows) {
    facilityRemaining.set(r.recordId, (facilityRemaining.get(r.recordId) ?? 0) + r.remainingBalance);
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
      <table className="min-w-full text-xs">
        <thead className="bg-zinc-100 text-[10px] uppercase tracking-wide text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
          <tr>
            <th className="px-2 py-2 text-left">Facility</th>
            <th className="px-2 py-2 text-left">#</th>
            <th className="px-2 py-2 text-left">Due</th>
            <th className="px-2 py-2 text-left">Paid</th>
            <th className="px-2 py-2 text-right">Days</th>
            <th className="px-2 py-2 text-right">EMI</th>
            <th className="px-2 py-2 text-right">Original</th>
            <th className="px-2 py-2 text-right">Waived</th>
            <th className="px-2 py-2 text-right">Collected</th>
            <th className="px-2 py-2 text-right">Remaining</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
          {rows.map((r) => (
            <tr key={r.id} className={r.remainingBalance > 0 ? 'bg-rose-50/30' : undefined}>
              <td className="px-2 py-1.5 font-medium">{r.facility}</td>
              <td className="px-2 py-1.5 tabular-nums">{r.installmentNo}</td>
              <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">{formatDisplayDate(r.dueDate)}</td>
              <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">
                {r.paidDate ? formatDisplayDate(r.paidDate) : '—'}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-rose-900">{r.overdueDays}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{formatInr(r.emiAmount)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{formatInr(r.originalAmount)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-violet-800">{formatInr(r.waivedAmount)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-emerald-800">{formatInr(r.collectedAmount)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-rose-900">
                {formatInr(r.remainingBalance)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canWrite ? (
        <div className="flex flex-wrap gap-2 border-t border-zinc-100 px-3 py-2 dark:border-zinc-800">
          {[...facilityRemaining.entries()].map(([recordId, remaining]) => {
            if (remaining <= 0) return null;
            const facility = customer.facilities.find((f) => f.recordId === recordId);
            if (!facility) return null;
            return (
              <button
                key={recordId}
                type="button"
                onClick={() => onWaive(facility, remaining)}
                className="inline-flex items-center gap-1 rounded-lg border border-violet-300 bg-violet-50 px-2.5 py-1 text-[11px] font-semibold text-violet-900 hover:bg-violet-100"
              >
                <Scale className="h-3 w-3" />
                Waive {facility.title} ({formatInr(remaining)})
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function OverdueWaiveModal({
  customer,
  facility,
  maxAmount,
  onClose,
}: {
  customer: UnifiedCustomer;
  facility: UnifiedLoanFacility;
  maxAmount: number;
  onClose: () => void;
}) {
  const [amount, setAmount] = useState(String(Math.round(maxAmount)));
  const [reason, setReason] = useState('');
  const [paidAt, setPaidAt] = useState(defaultAdminEntryDate());
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const amt = parseAmountInput(amount);
    if (amt <= 0) {
      toast.error('Enter a valid waiver amount.');
      return;
    }
    if (!reason.trim()) {
      toast.error('A reason is required.');
      return;
    }
    setBusy(true);
    const res = await recordOverdueWaiver({
      customerId: customer.id,
      recordId: facility.recordId,
      facilityTitle: facility.title,
      kind: facility.kind,
      amount: amt,
      reason: reason.trim(),
      paidAt: adminEntryIso(paidAt),
      facility,
    });
    setBusy(false);
    if (!res.ok) {
      toast.error(res.error || 'Could not record waiver.');
      return;
    }
    toast.success('Overdue waiver recorded.');
    onClose();
  };

  return (
    <MobileModalPortal onClose={onClose}>
      <div className="w-full max-w-md rounded-t-2xl border border-zinc-200 bg-white p-4 shadow-2xl dark:border-zinc-700 dark:bg-zinc-900 sm:rounded-2xl sm:p-5">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-violet-800">Waive overdue</p>
            <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">{customer.name}</h3>
            <p className="text-xs text-zinc-500">{facility.title} · max {formatInr(maxAmount)}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-zinc-500 hover:bg-zinc-100">
            ×
          </button>
        </div>
        <div className="space-y-3">
          <label className="block text-xs font-medium text-zinc-700 dark:text-zinc-300">
            Waiver amount
            <AmountInput value={amount} onChange={setAmount} className="mt-1 w-full" />
          </label>
          <label className="block text-xs font-medium text-zinc-700 dark:text-zinc-300">
            Effective date
            <input
              type="date"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-800"
            />
          </label>
          <label className="block text-xs font-medium text-zinc-700 dark:text-zinc-300">
            Reason (audit trail)
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-800"
              placeholder="e.g. Customer hardship — owner approved partial waiver"
            />
          </label>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => void submit()}
            className="flex-1 rounded-lg bg-violet-700 py-2.5 text-sm font-semibold text-white hover:bg-violet-800 disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Record waiver'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-zinc-300 px-4 py-2.5 text-sm font-semibold text-zinc-700"
          >
            Cancel
          </button>
        </div>
      </div>
    </MobileModalPortal>
  );
}
